import React, { useState, useEffect } from "react";
import { Navigation, MapPin, Loader2, Map as MapIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import AddressAutocomplete from "./AddressAutocomplete";
import LocationPicker from "./LocationPicker";
import { useLocationCtx } from "../../context/LocationContext";

// Global "set your location" modal shown to customers on app open.
export default function LocationGate() {
  const { location, locating, setLocation, requestCurrentLocation, promptOpen, closePrompt, initialDraft } = useLocationCtx();
  const [draft, setDraft] = useState(location);
  const [pickText, setPickText] = useState("");
  const [showMap, setShowMap] = useState(false);

  // When auto-detect seeds a pin, open on the map so the customer can fine-tune before saving.
  useEffect(() => {
    if (promptOpen && initialDraft) { setDraft(initialDraft); setShowMap(true); }
  }, [promptOpen, initialDraft]);

  const onCurrent = async () => {
    try { const loc = await requestCurrentLocation(); setDraft(loc); }
    catch { toast.error("Couldn't get your location. Search your area or set it on the map instead."); setShowMap(true); }
  };

  const confirm = () => {
    if (!draft || !Number.isFinite(draft.lat)) { toast.error("Please set your location first."); return; }
    setLocation(draft);
    closePrompt();
  };

  return (
    <Dialog open={promptOpen} onOpenChange={(o) => { if (!o) closePrompt(); }}>
      <DialogContent className="max-w-md" data-testid="location-gate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-brand" /> Where are you right now?</DialogTitle>
          <DialogDescription>{initialDraft ? "We detected your location — drag the pin to fine-tune it, then continue." : "We use your location to show the nearest restaurants, private cars and driver partners."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Button onClick={onCurrent} disabled={locating} className="w-full rounded-xl" data-testid="location-gate-current">
            {locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Navigation className="mr-2 h-4 w-4" />} Use my current location
          </Button>

          {draft?.label && (
            <div className="flex items-start gap-2 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-sm" data-testid="location-gate-selected">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <span className="text-ink-soft">Selected: <strong className="text-ink">{draft.label}</strong></span>
            </div>
          )}

          <div className="relative">
            <p className="mb-1 text-xs font-medium text-ink-muted">Or search an area / town</p>
            <AddressAutocomplete
              value={pickText}
              onTextChange={setPickText}
              onSelect={({ address, lat, lng }) => { if (lat != null) setDraft({ lat, lng, label: address }); }}
              placeholder="Search your area or address"
              allowDetect={false}
              showQuick={false}
              testId="location-gate-search"
            />
          </div>

          <button type="button" onClick={() => setShowMap((s) => !s)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand hover:underline" data-testid="location-gate-map-toggle">
            <MapIcon className="h-3.5 w-3.5" /> {showMap ? "Hide map" : "Set exact spot on map"}
          </button>
          {showMap && (
            <LocationPicker
              value={draft ? { lat: draft.lat, lng: draft.lng, address: draft.label } : undefined}
              onChange={({ lat, lng, address }) => setDraft({ lat, lng, label: address || "Pinned location" })}
            />
          )}

          <Button onClick={confirm} variant="default" className="w-full rounded-xl bg-graphite text-white hover:bg-graphite-600" data-testid="location-gate-confirm">
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
